import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import SectionTitle from 'ui/atoms/SectionTitle';
import * as yup from 'yup';
import { useTranslation, Trans } from 'react-i18next';
import { Spacing, Input, Button, Text } from '@reservamos/elements';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';

/**
 * createValidationSchema Functional Component
 *
 * @param {Function} t - The translation function.
 *
 * @returns {Object} The validation schema.
 */
const createValidationSchema = (t) =>
  yup.object().shape({
    operationNumber: yup
      .string()
      .trim()
      .required(t('validation.operation_number_required'))
      .length(6, t('validation.operation_number_invalid')),
    email: yup
      .string()
      .trim()
      .required(t('validation.email_required'))
      .email(t('validation.email_invalid')),
  });

/**
 * TermsAndConditionsLink Functional Component
 *
 * @param {Function} t - The translation function.
 * @returns {JSX.Element} The rendered terms and conditions link component.
 */
const TermsAndConditionsLink = ({ t }) => {
  const env = useWhitelabelEnvs();
  const { exchangeContactData } = env;

  if (
    !exchangeContactData?.termsAndConditionsLink ||
    exchangeContactData.termsAndConditionsLink.trim() === ''
  ) {
    return null;
  }

  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
    <a
      href={exchangeContactData.termsAndConditionsLink}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:text-blue-800 underline"
    >
      {t('modal_confirm_reschedule.terminos_link')}
    </a>
  );
};

TermsAndConditionsLink.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * RequiredFieldsList Functional Component
 *
 * @param {Object} props - The component props.
 * @param {Function} props.t - The translation function.
 *
 * @returns {JSX.Element} The rendered required fields list component.
 */
const RequiredFieldsList = ({ t }) => (
  <>
    <Text size="M" className="mb-3">
      {t('labels.purchase_data')}
    </Text>
    <ul className="list-disc list-inside">
      <li className="text-base">
        <Text elementType="span" size="M">
          {t('labels.reservations_code')}
        </Text>
      </li>
      <li className="text-base">
        <Text elementType="span" size="M">
          {t('labels.email')}
        </Text>
      </li>
    </ul>
  </>
);

RequiredFieldsList.propTypes = {
  t: PropTypes.func.isRequired,
};

/**
 * FormFields Functional Component
 *
 * @param {Object} props - The component props.
 * @param {Object} props.values - The form values.
 * @param {Function} props.handleChange - The function to handle changes.
 * @param {Function} props.handleBlur - The function to handle blur.
 * @param {Object} props.errors - The form errors.
 * @param {Object} props.touched - The form touched fields.
 * @param {Function} props.t - The translation function.
 *
 * @returns {JSX.Element} The rendered form fields component.
 */
const FormFields = ({ values, handleChange, handleBlur, errors, touched, t }) => (
  <Spacing flexGrow isResponsive responsiveSize="S">
    <Input
      autoComplete="off"
      id="operationNumber"
      name="operationNumber"
      label={t('labels.reservations_code')}
      maxLength={6}
      errorMessage={errors.operationNumber}
      hasError={errors.operationNumber !== undefined && touched.operationNumber}
      onBlur={handleBlur}
      onChange={handleChange}
      value={values.operationNumber}
    />
    <Input
      id="email"
      name="email"
      label={t('labels.email')}
      maxLength={60}
      type="email"
      autoComplete="email"
      errorMessage={errors.email}
      hasError={errors.email !== undefined && touched.email}
      onBlur={handleBlur}
      onChange={handleChange}
      value={values.email}
    />
  </Spacing>
);

FormFields.propTypes = {
  values: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

/**
 * CDSForm Functional Component
 *
 * @param {Object} props - The component props.
 * @param {Function} props.handleSubmit - The function to be called when the form is submitted.
 *
 * @returns {JSX.Element} The rendered form component.
 */
const CDSForm = ({ handleSubmit }) => {
  const { t } = useTranslation('exchange');

  const initialValues = {
    operationNumber: '',
    email: '',
  };

  const validationSchema = createValidationSchema(t);

  /**
   * This function returns the title and description for the section title.
   * @returns An object with a `title` and `description` property.
   */
  const getSectionTitleContent = () => ({
    title: t('title.my_travels'),
    description: (
      <Trans t={t} i18nKey="subtitle.exchange_cancelations_reschedule">
        <TermsAndConditionsLink t={t} />
      </Trans>
    ),
  });

  const sectionTitleContent = getSectionTitleContent();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) =>
        handleSubmit({
          operationNumbers: [values.operationNumber],
          email: values.email,
        })
      }
    >
      {({ values, handleChange, handleBlur, errors, touched }) => (
        <Form className="pb-6">
          <Spacing vertical responsiveSize="XL">
            <SectionTitle
              title={sectionTitleContent.title}
              description={sectionTitleContent.description}
            />

            <RequiredFieldsList t={t} />

            <FormFields
              values={values}
              handleChange={handleChange}
              handleBlur={handleBlur}
              errors={errors}
              touched={touched}
              t={t}
            />

            <Spacing justifyContent="space-between" alignItems="center" isResponsive>
              <Text size="S" weight="bold">
                {t('info.available_refund_message')}
              </Text>
              <Button
                buttonType="submit"
                text={t('button.enter')}
                variant="accent"
                isRounded
                withHeight
                fullWidthOnSmall
              />
            </Spacing>
          </Spacing>
        </Form>
      )}
    </Formik>
  );
};

const propTypes = {
  handleSubmit: PropTypes.func.isRequired,
};

CDSForm.propTypes = propTypes;

export default CDSForm;
